<template>
  <div class="document">
    <div class="col sub-sub-header hide-medium">
      <a :class="{ highlight: highlight === 'document-kbis' }" class="link" @click="() => goToAndRemoveAnchor('btn-sub-header', 'document-kbis')">
        {{ $t('extractKbis') }}
      </a>
      <a
        v-if="user.client && user.client.effectiveBeneficiariesAccess"
        :class="{ highlight: highlight === 'document-beneficiary' }"
        class="link"
        @click="() => goToAndRemoveAnchor('document-beneficiary', 'document-beneficiary')"
      >
        {{ $t('declarationBeneficialOwners') }}
      </a>
      <a :class="{ highlight: highlight === 'document-status' }" class="link" @click="() => goToAndRemoveAnchor('document-status', 'document-status')">
        {{ $t('actsAndStatutes') }}
      </a>
      <a
        :class="{ highlight: highlight === 'document-accounts-annual' }"
        class="link"
        @click="() => goToAndRemoveAnchor('document-accounts-annual', 'document-accounts-annual')"
      >
        {{ $t('annualAccounts') }}
      </a>
    </div>

    <div class="col document-kbis-container margin">
      <div v-if="loading && documentsAvailable && !documentsAvailable.initialDocumentsAvailable" class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('documentsInSynchronization') }}</div>
      </div>

      <div v-if="loading && documentsAvailable && !documentsAvailable.initialDocumentsAvailable" class="lds-ring blue">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>

      <div v-else-if="!loading && !results.length" class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('noDocumentsAvailable') }}</div>
      </div>

      <div v-else-if="!loading && error" class="info error">
        <div class="icon"></div>
        <div class="text">{{ $t('importationError') }}</div>
      </div>

      <div v-if="!loading && documentsAvailable && documentsAvailable.initialDocumentsAvailable && results.length" id="document-kis">
        <div class="form-container">
          <div class="input-title">{{ $t('period') }}</div>

          <Dropdown
            class="year-dropdown"
            :config="{ inputSize: 48, color: 'white', active: false }"
            :values="getYear()"
            :placeholder="''"
            :on-change="
              value => {
                selectedYear = value.year;
                getDocuments();
              }
            "
            :value="selectedYear"
          >
            <template #placeholder="{ value, isPlaceholder }">
              <div v-if="!isPlaceholder" class="year">{{ value }}</div>
              <div v-else class="year grey">{{ value }}</div>
            </template>
            <template #choices="{ choice }">
              <div class="year white" :class="{ highlight: choice === selectedYear }">{{ choice.year }} ({{ choice.countTotal }})</div>
            </template>
          </Dropdown>
        </div>

        <div v-if="kbis.documents.length" class="title">{{ $t('extractKbis') }} ({{ kbis.countTotal }})</div>
        <DocumentsTable
          v-if="kbis.documents.length"
          :key="'kbis' + kbis.countTotal + pendingCount(kbis) + selectedYear"
          :fake-data="kbis.documents"
          :doc-type="kbis.docType"
        />

        <div v-if="beneficiary.documents.length && user.client && user.client.effectiveBeneficiariesAccess" id="document-beneficiary" class="title">
          {{ $t('declarationBeneficialOwners') }} ({{ beneficiary.countTotal }})
        </div>
        <DocumentsTable
          v-if="beneficiary.documents.length && user.client && user.client.effectiveBeneficiariesAccess"
          :key="'beneficiary' + beneficiary.countTotal + pendingCount(beneficiary) + selectedYear"
          :fake-data="beneficiary.documents"
          :doc-type="beneficiary.docType"
        />

        <div v-if="status.documents.length" id="document-status" class="title">{{ $t('actsAndStatutes') }} ({{ status.countTotal }})</div>
        <DocumentsTable
          v-if="status.documents.length"
          :key="'status' + status.countTotal + pendingCount(status) + selectedYear"
          :fake-data="status.documents"
          :doc-type="status.docType"
        />

        <div v-if="annualAccounts.documents.length" id="document-accounts-annual" class="title">
          {{ $t('annualAccounts') }} ({{ annualAccounts.countTotal }})
        </div>
        <DocumentsTable
          v-if="annualAccounts.documents.length"
          :key="'annualAccounts' + annualAccounts.countTotal + pendingCount(annualAccounts) + selectedYear"
          :fake-data="annualAccounts.documents"
          :doc-type="annualAccounts.docType"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import DocumentsTable from '@/components/tables/DocumentTable';
import throttle from '@/utils/throttle';

export default {
  components: { DocumentsTable, Dropdown },

  props: {
    isMonitored: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      renderComponent: true,
      selectedYear: this.$t('all'),
      loading: true,
      highlight: 'document-kbis',
      kbis: { documents: [], countTotal: 0 },
      beneficiary: { documents: [], countTotal: 0 },
      status: { documents: [], countTotal: 0 },
      annualAccounts: { documents: [], countTotal: 0 },
      results: [],
      documentsInfos: null,
      automaticFetch: null,
      documentsAvailable: null,
    };
  },

  computed: {
    ...mapGetters(['user']),
  },

  watch: {
    async documentsAvailable(value) {
      await this.getDocuments();

      if (value.initialDocumentsAvailable && value.initialDocumentsStatus === 4) {
        this.stopPolling();
        this.loading = false;
        this.error = true;
      } else if (value.initialDocumentsAvailable) {
        this.loading = false;

        if (this.hasPendingDocuments()) this.startPolling();
        else this.stopPolling();
      }
    },
  },

  async mounted() {
    this.loading = true;

    await this.checkDocumentAvailability();

    if (!this.documentsAvailable.initialDocumentsAvailable) this.startPolling();

    window.addEventListener('scroll', this.handleScroll);

    this.documentsInfos = await this.$store.dispatch('getCompanyDocumentInfos', {
      searchedCompanySiren: this.$route.params.id,
    });
  },

  created() {
    if (this.isMonitored) return;

    this.$router.push({ name: 'searchByIdEnterprise', replace: true });
  },

  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
    this.stopPolling();
  },

  methods: {
    getYear() {
      if (!this.documentsInfos || !this.documentsInfos.years) return [];
      const countTotal = this.documentsInfos.years.reduce((total, doc) => {
        total += doc.countTotal;
        return total;
      }, 0);

      return [{ year: this.$t('all'), countTotal }].concat(this.documentsInfos.years);
    },

    goToAndRemoveAnchor(anchor, highlight) {
      this.highlight = highlight;
      const url = location.href;
      location.href = '#' + anchor;
      history.replaceState(null, null, url);
    },

    async getData() {
      const types = [1, 8, 2, 4];

      types.map(async type => {
        const data = this.results.find(e => e.docType === type);

        switch (type) {
          case 1:
            this.kbis = data || { documents: [], countTotal: 0 };
            break;
          case 2:
            this.status = data || { documents: [], countTotal: 0 };
            break;
          case 4:
            this.annualAccounts = data || { documents: [], countTotal: 0 };
            break;
          case 8:
            this.beneficiary = data || { documents: [], countTotal: 0 };
            break;
        }

        await this.forceRerender();
      });
    },

    async getDocuments() {
      await this.$store
        .dispatch('getCompanyDocument', {
          searchedCompanySiren: this.$route.params.id,
          year: this.selectedYear !== this.$t('all') ? this.selectedYear : undefined,
        })
        .then(results => (this.results = results))
        .catch(error => console.error(error));

      await this.getData();
    },

    async forceRerender() {
      this.renderComponent = false;

      void this.$nextTick(() => {
        this.renderComponent = true;
      });
    },

    isInViewport(anchor) {
      const el = document.querySelector(`#${anchor}`);
      if (!document.querySelector(`#${anchor}`)) return;
      return this.$utils.isInViewport(el);
    },

    handleScroll: throttle(function () {
      if (!this) return;

      if (this.isInViewport('document-accounts-annual')) {
        this.highlight = 'document-accounts-annual';
        if (this.isInViewport('btn-sub-header')) {
          this.highlight = 'document-kbis';
          return;
        }
        return;
      }

      if (this.isInViewport('document-beneficiary')) {
        this.highlight = 'document-beneficiary';
        if (this.isInViewport('document-status')) {
          this.highlight = 'document-status';
          return;
        }
        return;
      }

      if (this.isInViewport('document-status')) {
        this.highlight = 'document-status';
        if (this.isInViewport('document-beneficiary')) {
          this.highlight = 'document-beneficiary';
          return;
        }
        return;
      }

      if (this.isInViewport('btn-sub-header')) {
        this.highlight = 'document-kbis';
        if (this.isInViewport('document-beneficiary')) {
          this.highlight = 'document-beneficiary';
        }
      }
    }, 50),

    hasPendingDocuments() {
      return this.results.some(group => group.documents.some(doc => doc.status === 1));
    },

    pendingCount(group) {
      return group.documents.filter(doc => doc.status === 1).length;
    },

    startPolling() {
      if (this.automaticFetch) return;

      this.automaticFetch = setInterval(() => {
        this.checkDocumentAvailability();
      }, 1500);
    },

    stopPolling() {
      clearInterval(this.automaticFetch);
      this.automaticFetch = null;
    },

    async checkDocumentAvailability() {
      this.documentsAvailable = await this.$store.dispatch('getPortfolioBySiren', {
        siren: this.$route.params.id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.document {
  width: 100%;
  display: flex;
  padding: 80px 12px 80px;
  box-sizing: border-box;

  .col {
    &.sub-sub-header {
      flex-grow: 1;
      width: 200px;
      max-height: 200px;
      padding-right: 36px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      position: sticky;
      top: 110px;

      .link {
        margin: 0 0 20px;
        font-weight: 500;
        font-size: 18px;
        color: colors.$grey-color;
        width: 100%;

        &.highlight {
          color: colors.$black-color;
        }

        &:hover {
          cursor: pointer;
          color: rgba(colors.$black-color, 0.8);
        }
      }
    }

    &.document-kbis-container {
      min-height: 40px;
      display: flex;
      padding-left: 80px;
      border-left: 1px solid colors.$lightgrey-color;
      flex-grow: 1;
      flex-direction: column;
      width: 100%;

      .form-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        margin-right: 8px;

        .year-dropdown {
          width: 128px;
        }

        .input-title {
          width: 100%;
          color: colors.$darkgrey-color;
          font-weight: 500;
          font-size: 16px;
          margin: 24px 0 10px 0;
        }
      }

      &.margin {
        margin-bottom: 386px;
      }

      .lds-ring {
        margin-top: 60px;
        width: 100%;
      }

      .info {
        display: flex;
        align-items: center;
        margin: 14px 0 0 0;
        font-size: 14px;

        &.error {
          .text,
          .icon {
            color: colors.$red-color;
            border-color: colors.$red-color;
          }

          .icon::after,
          .icon::before {
            background: colors.$red-color;
          }
        }

        &.margin-top {
          margin: 64px 0 0;
        }

        .icon {
          margin: 0 12px 0 0;
          position: relative;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid colors.$lightblue-color;
          flex-shrink: 0;

          &:after {
            position: absolute;
            content: '';
            bottom: 3px;
            left: calc(50% - 1px);
            right: calc(50% - 1px);
            height: 8px;
            background: colors.$lightblue-color;
          }

          &:before {
            position: absolute;
            content: '';
            top: 3px;
            left: calc(50% - 1px);
            right: calc(50% - 1px);
            height: 2px;
            background: colors.$lightblue-color;
          }
        }

        .text {
          color: colors.$lightblue-color;
          font-size: 14px;
          opacity: 0.8;
          &.black {
            color: colors.$black-color;
            margin-bottom: 14px;
            font-weight: 500;
            font-size: 14px;
            line-height: 16px;
            display: flex;
            align-items: center;
            text-decoration-line: underline;
          }
        }
      }

      .year-dropdown {
        width: 128px;
      }

      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 20px;
        margin: 0 0 24px;

        &:not(:first-child) {
          padding-top: 80px;
        }
      }

      .people-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        background: colors.$unread-table-color;
        margin: 20px 0;
        border-radius: 5px;
        padding: 30px;

        .title {
          font-weight: 500;
          font-size: 14px;
          color: colors.$lightblue-color;
          align-self: flex-start;
          margin: 0;
          height: 24px;
        }

        .people-content {
          flex-grow: 1;
          padding: 20px 0 20px;
          position: relative;
          width: 100%;
          display: flex;

          &:before {
            position: absolute;
            content: '';
            top: 0;
            height: 1px;
            width: 100%;
            background: rgba(colors.$lightblue-color, 0.3);
          }

          .item {
            padding: 0 20px;
            font-size: 13px;
            color: colors.$darkgrey-color;
            min-width: 120px;

            &:first-child {
              padding-left: 0;
            }

            &:last-child {
              padding-right: 0;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .document-kbis-container {
    border: none !important;
    padding-left: 0 !important;
  }

  .title {
    padding-top: 24px !important;
  }
}
</style>
